import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../../context/AdminContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faLayerGroup, faList, faUtensils } from '@fortawesome/free-solid-svg-icons';
import MenuVisualizationModal from '../modals/MenuVisualizationModal';
import AdminPageHeader from '../shared/AdminPageHeader';
import AdminMetricStrip from '../shared/AdminMetricStrip';
import AdminEmptyState from '../shared/AdminEmptyState';
import AdminSectionCard from '../shared/AdminSectionCard';
import '../../styles/route_pages/Menus.css';

const Menus = () => {
  const { menus, categories, items } = useAdmin();
  const [isVisualizationModalVisible, setIsVisualizationModalVisible] = useState(false);
  const navigate = useNavigate();

  const venueMenu = useMemo(() => menus[0] || null, [menus]);

  const menuStats = useMemo(() => {
    if (!venueMenu) {
      return { categoriesCount: 0, itemsCount: 0 };
    }

    const menuCategories = categories.filter((cat) => cat.menuId === venueMenu.id && !cat.isSystemCategory);
    const allMenuCategories = categories.filter((cat) => cat.menuId === venueMenu.id);
    const activeMenuItems = items.filter(
      (item) => allMenuCategories.some((cat) => cat.id === item.categoryId) && item.status === 'active',
    );

    return {
      categoriesCount: menuCategories.length,
      itemsCount: activeMenuItems.length,
    };
  }, [categories, items, venueMenu]);

  const metrics = [
    {
      label: 'Kategori',
      value: menuStats.categoriesCount,
      helper: 'Sistem kategorileri hariç toplam görünür kategori',
    },
    {
      label: 'Aktif Ürün',
      value: menuStats.itemsCount,
      helper: 'Menüde yayında olan aktif ürünler',
    },
  ];

  if (!venueMenu) {
    return (
      <div className="page-container">
        <AdminPageHeader title="Menü Yönetimi" subtitle="Menü yapısını, kategorileri ve ürün akışını tek yerden yönetin." />
        <AdminEmptyState
          icon={faLayerGroup}
          title="Henüz menü bulunamadı"
          description="Menü oluşturmak için sistem yöneticinizle iletişime geçin."
        />
      </div>
    );
  }

  return (
    <div className="page-container">
      <AdminPageHeader
        title="Menü Yönetimi"
        subtitle="Menü genel görünümünü kontrol edin ve kategori ya da ürün akışına hızla geçin."
        actions={(
          <button className="btn btn-secondary" onClick={() => setIsVisualizationModalVisible(true)}>
            <FontAwesomeIcon icon={faEye} />
            Menüyü Görüntüle
          </button>
        )}
      />

      <AdminMetricStrip items={metrics} />

      <div className="admin-page-stack">
        <AdminSectionCard title="Menü Özeti">
          <div className="menus-overview-grid">
            <div className="admin-overview-card menus-hero-card">
              <div className="menus-hero-header">
                <div className="admin-symbol-token menus-hero-token">
                  <FontAwesomeIcon icon={faLayerGroup} />
                </div>
                <div>
                  <div className="admin-overview-label">Aktif Menü</div>
                  <div className="menus-hero-title">{venueMenu.name}</div>
                </div>
              </div>
            </div>
            <div className="admin-overview-card">
              <div className="admin-overview-label">Kategori Yoğunluğu</div>
              <div className="admin-overview-value">{menuStats.categoriesCount}</div>
            </div>
            <div className="admin-overview-card">
              <div className="admin-overview-label">Aktif Ürün</div>
              <div className="admin-overview-value">{menuStats.itemsCount}</div>
            </div>
          </div>
        </AdminSectionCard>

        <AdminSectionCard title="Hızlı Yönetim Girişleri">
          <div className="menus-action-grid">
            <button type="button" className="menus-action-card" onClick={() => navigate('/admin/categories')}>
              <div className="menus-action-icon">
                <FontAwesomeIcon icon={faList} />
              </div>
              <div className="menus-action-body">
                <h3>Kategorileri Yönet</h3>
                <span className="admin-chip admin-chip-accent">{menuStats.categoriesCount} kategori</span>
              </div>
            </button>

            <button type="button" className="menus-action-card" onClick={() => navigate('/admin/items')}>
              <div className="menus-action-icon">
                <FontAwesomeIcon icon={faUtensils} />
              </div>
              <div className="menus-action-body">
                <h3>Ürünleri Yönet</h3>
                <span className="admin-chip admin-chip-success">{menuStats.itemsCount} aktif ürün</span>
              </div>
            </button>
          </div>
        </AdminSectionCard>
      </div>

      <MenuVisualizationModal show={isVisualizationModalVisible} onClose={() => setIsVisualizationModalVisible(false)} menu={venueMenu} />
    </div>
  );
};

export default Menus;
